/**
 * vec3 — minimal 3D vector helpers (cross / length / normalise).
 *
 * The ONE monorepo-wide source of truth for pure 3D vector math. Originally lived in
 * `packages/command-engine/src/vec3.ts` ("the lowest shared package" at the time — OCS→WCS
 * lift in `apps/web` print/pdf, the block-explode ellipse arm, and command-engine's own
 * ellipse-explode geometry all depended on it). T1 arm (c)'s WCS bounds helper
 * (`./wcsBoundsMath.ts`) needs the SAME cross-product math for its own OCS→WCS Arbitrary Axis
 * Algorithm, and `drawing-model` is BELOW `command-engine` in the dependency graph
 * (`command-engine/package.json` depends on `drawing-model`, never the reverse) — so this is
 * now the true "lowest shared package". Relocated here (not re-derived) so the two copies can
 * never drift (CLAUDE.md dup:check one-truth); `command-engine/src/vec3.ts` re-exports these
 * symbols so every existing `from "@dxf-ai-editor/command-engine"` import site (and the
 * `apps/web` `geometry/vec3.ts` sibling that re-exports THAT) keeps working unchanged.
 *
 * Pure math, NO CAD semantics, imports nothing — a leaf module.
 */

/** A 3D vector. */
export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** Cross product a × b. */
export function cross(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}

/** Dot product a · b. */
export function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

/** Euclidean length |v|. */
export function length(v: Vec3): number {
  return Math.hypot(v.x, v.y, v.z);
}

/** Unit vector; returns (0,0,0) for a zero-length (degenerate) vector. */
export function normalize(v: Vec3): Vec3 {
  const len = length(v);
  if (len === 0) return { x: 0, y: 0, z: 0 };
  return { x: v.x / len, y: v.y / len, z: v.z / len };
}
